/**
 * Excel Utility Functions for Cloudburst Reports
 * MATCHES OFFICIAL WEB UI COLUMNS AND FORMATS
 */

export const cloudburstMISConfig = {
    dist_date: { title: 'DIST_DATE_CLOUD_BURST_REPORT', hasDate: true, hasBlock: true, dateFirst: false },
    state_date: { title: 'STATE_DATE_CLOUD_BURST_REPORT', hasDate: true, hasBlock: false, dateFirst: true },
    dist: { title: 'DIST_CLOUD_BURST_REPORT', hasDate: false, hasBlock: true, dateFirst: false },
    state: { title: 'STATE_CLOUD_BURST_REPORT', hasDate: false, hasBlock: false, dateFirst: false }
};

export const CLOUDBURST_MIS_FIELDS = {
    'no_of_deaths_today': 'Number of Deaths due to cloud burst (Today)',
    'no_of_deaths_cum': 'Number of Deaths (Cumulative)',
    'no_of_dec_ex_gratia_pay_done_today': 'Number of deceased person for whom ex-gratia payment done (Today)',
    'no_of_dec_ex_gratia_pay_done_cum': 'Number of deceased person for whom ex-gratia payment done (Cumulative)',
    'no_of_persons_hospitalized_today': 'Number of persons hospitalized (Today)',
    'no_of_persons_hospitalized_cum': 'Number of persons hospitalized (Cumulative)',
    'no_of_persons_hospitalized_pay_done_today': 'Number of hospitalized person for whom payment done (Today)',
    'no_of_persons_hospitalized_pay_done_cum': 'Number of hospitalized person for whom payment done (Cumulative)',
    'remarks': 'Remarks'
};

export const getTimestamp = () => {
  const now = new Date();
  now.setMinutes(now.getMinutes() + 330); 
  const istDate = now.toISOString().split("T")[0];
  const istTime = now.toISOString().split("T")[1].split(".")[0].replace(/:/g, "");
  return `${istDate}_${istTime}`;
};

export const formatDateForMISExcel = (dateValue) => {
    if (!dateValue) return "";
    try {
      if (typeof dateValue === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateValue)) return dateValue;
      const date = new Date(dateValue);
      if (isNaN(date.getTime())) return String(dateValue);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    } catch (e) {
      return String(dateValue);
    }
};

export const transformDataForExcel = (data, columnOrder) => {
    if (!data || data.length === 0) return { columns: [], transformedData: [] };
    const columnMapping = {
      dist_name: "Name",
      total_block_count: "Total Blocks",
      report_submitted: "Report Submitted",
      report_not_submitted: "Report Not Submitted",
    };
    const columns = columnOrder.map(key => ({
      label: columnMapping[key] || key.toUpperCase().replace(/_/g, " "),
      key: key,
    }));
    const transformedData = data.map(row => {
      const transformedRow = {};
      columns.forEach(col => { transformedRow[col.label] = row[col.key] ?? ""; });
      return transformedRow;
    });
    return { columns, transformedData };
};

export const transformMISDataForExcel = (rawData, viewType) => {
    let dailyRecords = [];
    if (Array.isArray(rawData)) dailyRecords = rawData;
    else if (rawData.mis_data) dailyRecords = rawData.mis_data.heatwave || rawData.mis_data.cloudburst || rawData.mis_data.dr_cloud_burst || [];
    else dailyRecords = rawData.dr_cloud_burst || rawData.heatwave || rawData.cloudburst || [];

    // ✅ FIX 1: Sort the daily records by date in Ascending order (oldest to newest)
    dailyRecords.sort((a, b) => {
        const valA = Number(a.reported_on) || 0;
        const valB = Number(b.reported_on) || 0;
        return valA - valB;
    });

    let cumCumRecords = [];
    if (Array.isArray(rawData.cumulative_data)) {
        cumCumRecords = rawData.cumulative_data;
    } else if (rawData.cumulative_data) {
        cumCumRecords = rawData.cumulative_data.heatwave_cum || rawData.cumulative_data.cloudburst_cum || [];
    } else if (rawData.mis_data) {
        cumCumRecords = rawData.mis_data.heatwave_cum || rawData.mis_data.cloudburst_cum || [];
    } else {
        cumCumRecords = rawData.dr_cloud_burst_cumm || rawData.heatwave_cum || rawData.cloudburst_cum || [];
    }

    const config = cloudburstMISConfig[viewType] || cloudburstMISConfig.dist_date;
    const getId = (obj) => obj.dist_id || obj.region_id || obj.block_id || obj.id || 'NA';

    const cumMap = {};
    if (cumCumRecords && cumCumRecords.length > 0) {
        cumCumRecords.forEach(r => {
            const id = getId(r);
            const dateKey = r.reported_on || 'NO_DATE';
            cumMap[`${id}_${dateKey}`] = r;
        });
    }

    const headers = [];
    if (config.dateFirst) {
        if (config.hasDate) headers.push('Date');
        headers.push('District Name');
    } else {
        headers.push('District Name');
        if (config.hasDate) headers.push('Date');
    }
    if (config.hasBlock) headers.push('Block Name');

    Object.values(CLOUDBURST_MIS_FIELDS).forEach(label => headers.push(label));

    const rows = dailyRecords.map(rec => {
        const row = [];
        const id = getId(rec);
        const dateKey = rec.reported_on || 'NO_DATE';
        const cumRec = cumMap[`${id}_${dateKey}`] || {};

        if (config.dateFirst) {
            if (config.hasDate) row.push(formatDateForMISExcel(rec.reported_on));
            row.push(rec.dist_name || 'NA');
        } else {
            row.push(rec.dist_name || 'NA');
            if (config.hasDate) row.push(formatDateForMISExcel(rec.reported_on));
        }
        if (config.hasBlock) row.push(rec.block_name || 'NA');

        Object.keys(CLOUDBURST_MIS_FIELDS).forEach(apiField => {
            if (apiField === 'remarks') {
                // ✅ FIX 2: Force remarks to be "NA" at all times
                row.push('NA');
            } else if (apiField.endsWith('_cum')) {
                let val = cumRec[apiField];
                row.push((val !== null && val !== undefined) ? val : 0);
            } else {
                let val = rec[apiField];
                row.push((val !== null && val !== undefined) ? val : 0);
            }
        });
        return row;
    });

    // ✅ FIX 3: Ensure TOTAL row generates for EVERY view, placing "TOTAL" and "NA" dynamically
    if (rows.length > 0) {
        const totalRow = Array(headers.length).fill('');
        
        headers.forEach((colName, index) => {
            if (colName === 'District Name') {
                totalRow[index] = 'TOTAL';
            } else if (colName === 'Date' || colName === 'Block Name' || colName === 'Remarks') {
                totalRow[index] = 'NA';
            } else {
                // It's a metric column, so sum it up
                let sum = 0;
                rows.forEach(r => {
                    const val = parseInt(r[index], 10);
                    if (!isNaN(val)) sum += val;
                });
                totalRow[index] = sum;
            }
        });
        
        rows.push(totalRow);
    }

    return { headers, rows };
};